import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, IconButton, InputAdornment, LinearProgress, Paper, Stack, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { Clear, Download, EditNoteOutlined, LockOutlined, Search, VisibilityOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import { examService } from '../../services/examService';
import { subjectService } from '../../services/subjectService';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { useSessionContext } from '../../context/SessionContext';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useDebounce } from '../../hooks/useDebounce';
import type { Session, SessionClass } from '../../types/sessions';
import type { ExamGroupRow, Subject } from '../../types';
import type { MarksSheetPage, MarksSheetRow, SheetStatus } from '../../types/examination';
import FilterBar from '../../components/common/FilterBar';
import SearchableSelect from '../../components/common/SearchableSelect';
import EmptyState from '../../components/common/EmptyState';
import TableRowSkeletons from '../../components/common/TableRowSkeletons';
import { ToneChip, ChipTone } from '../../components/common/StatusChip';
import { tableHeadCellSx } from '../../components/admissions/tabLayout';
import { buildSessionOptions } from '../../utils/sessionOptions';
import { fmtClassName, usePdfExport, useSchoolBranding } from '../../utils/pdf';
import MarksReportDocument from '../../components/examination/pdf/MarksReportDocument';
import BusyIcon from '../../components/common/BusyIcon';
import LastEdited from '../../components/examination/LastEdited';
import MarksEntryDialog, { MarksSheetTarget } from '../../components/examination/marksEntry/MarksEntryDialog';

const STATUS: Record<SheetStatus, { label: string; tab: string; tone: ChipTone; empty: string }> = {
  pending: { label: 'Not started', tab: 'Not started', tone: 'warning', empty: 'Every sheet has been started' },
  draft: { label: 'In progress', tab: 'In progress', tone: 'info', empty: 'Nothing in progress' },
  locked: { label: 'Submitted', tab: 'Submitted', tone: 'success', empty: 'Nothing submitted yet' },
};
const TABS: Array<SheetStatus | 'all'> = ['all', 'pending', 'draft', 'locked'];
const EMPTY_COUNTS: MarksSheetPage['counts'] = { pending: 0, draft: 0, locked: 0 };

const dateRange = (a: string, b: string) => {
  const s = new Date(a); const e = new Date(b);
  return format(s, 'd MMM') === format(e, 'd MMM') ? format(s, 'd MMM yyyy') : `${format(s, 'd MMM')} – ${format(e, 'd MMM yyyy')}`;
};

/**
 * Enter Marks — every marks sheet of the session (a subject of an exam, for a
 * section or a whole class), with how far it has got and who teaches it. A
 * teacher sees the subjects they are assigned. Sheets still to start come
 * first, then those in progress, then submitted ones. Paged on the server.
 */
const EnterMarksPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const { activeSession } = useSessionContext();
  const canEnter = canWrite('enter_marks');

  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [exams, setExams] = useState<ExamGroupRow[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [examGroupId, setExamGroupId] = useState(() => new URLSearchParams(window.location.search).get('examGroupId') ?? '');
  const [classId, setClassId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [status, setStatus] = useState<SheetStatus | 'all'>('all');
  const [byClass, setByClass] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [data, setData] = useState<MarksSheetPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState<MarksSheetTarget | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([sessionsApi.list(), classesApi.list({ relationship: 'subject-teacher' }), subjectService.getSubjects({ limit: 500 })])
      .then(([s, c, sub]) => { setSessions(s); setClasses(c); setSubjects(sub.subjects); })
      .catch((err: any) => enqueueSnackbar(err?.message || 'Could not load the filters', { variant: 'error' }));
  }, [enqueueSnackbar]);
  useEffect(() => { if (!sessionId && activeSession) setSessionId(activeSession._id); }, [activeSession, sessionId]);

  // The session's published exams, for the Exam filter.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    examService.getExamGroups({ sessionId, status: 'published', limit: 100 })
      .then(res => { if (!cancelled) setExams(res.data?.groups ?? []); })
      .catch((err: any) => { if (!cancelled) enqueueSnackbar(err?.message || 'Could not load the exams', { variant: 'error' }); });
    return () => { cancelled = true; };
  }, [sessionId, enqueueSnackbar]);

  useEffect(() => { setPage(0); }, [sessionId, examGroupId, classId, subjectId, status, byClass, search]);

  const seq = useRef(0);
  const load = useCallback(async () => {
    if (!sessionId) return;
    const mine = ++seq.current;
    setLoading(true);
    try {
      const res = await examService.listSheets({
        sessionId,
        examGroupId: examGroupId || undefined,
        classId: classId || undefined,
        subjectId: subjectId || undefined,
        status: status === 'all' ? undefined : status,
        search: search.trim() || undefined,
        byClass,
        page: page + 1,
        limit: rowsPerPage,
      });
      if (mine === seq.current) setData(res);
    } catch (err: any) {
      if (mine === seq.current) { enqueueSnackbar(err?.message || 'Could not load the marks sheets', { variant: 'error' }); setData(null); }
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [sessionId, examGroupId, classId, subjectId, status, search, byClass, page, rowsPerPage, enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);

  const sessionOptions = useMemo(() => buildSessionOptions(sessions, theme), [sessions, theme]);
  const examOptions = useMemo(() => exams.map(e => ({ value: e.examGroupId, label: e.name, secondary: dateRange(e.startDate, e.endDate) })), [exams]);
  const classOptions = useMemo(() => classes.map(c => ({ value: c._id, label: fmtClassName(c.name) })), [classes]);
  const subjectOptions = useMemo(() => subjects.map(s => ({ value: s._id, label: s.name })), [subjects]);
  const counts = data?.counts ?? EMPTY_COUNTS;
  const rows = data?.items ?? [];
  const total = data?.pagination.totalItems ?? 0;
  const filtered = !!(examGroupId || classId || subjectId || search.trim());

  const open = (row: MarksSheetRow) => setTarget({
    examId: row.examId,
    classId: row.classId,
    sectionInstanceId: row.sectionInstanceId,
    subjectId: row.subjectId,
    examName: row.examName,
    className: fmtClassName(row.className),
    subjectName: row.subjectName,
    sectionLabel: row.sectionName ? `Section ${row.sectionName}` : 'Whole class',
    lastEdited: row.lastEdited,
  });

  // The sheet's marks report, drawn in the browser.
  const { generate: generatePdf } = usePdfExport();
  const { branding } = useSchoolBranding();
  const downloadPdf = async (row: MarksSheetRow) => {
    setDownloading(row.key);
    try {
      const name = ['marks', row.examName, fmtClassName(row.className), row.sectionName, row.subjectName].filter(Boolean).join('-').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      await generatePdf(async () => (
        <MarksReportDocument report={await examService.getMarksPrint(row)} branding={branding} generatedAt={new Date().toISOString()} />
      ), `${name}.pdf`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>Enter Marks</Typography>
          <Typography variant="body2" color="text.secondary">Each sheet is one subject of an exam. Save as you go; submit when every student has a mark.</Typography>
        </Box>
        <ToggleButtonGroup
          value={byClass ? 'class' : 'section'}
          exclusive
          size="small"
          onChange={(_, v) => v && setByClass(v === 'class')}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2, py: 0.5, fontWeight: 500 } }}
        >
          <ToggleButton value="section">By section</ToggleButton>
          <ToggleButton value="class">Whole class</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <FilterBar loading={loading && rows.length > 0}>
        <TextField
          size="small"
          placeholder="Search exams"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          sx={{ flex: 1, minWidth: 200, maxWidth: 300 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>,
            endAdornment: searchInput ? <InputAdornment position="end"><IconButton size="small" aria-label="Clear search" onClick={() => setSearchInput('')}><Clear fontSize="small" /></IconButton></InputAdornment> : undefined,
          }}
        />
        <SearchableSelect label="Session" value={sessionId} onChange={v => { setSessionId(v); setExamGroupId(''); }} options={sessionOptions} clearable={false} minWidth={200} />
        <SearchableSelect label="Exam" placeholder="All exams" value={examGroupId} onChange={setExamGroupId} options={examOptions} minWidth={200} />
        <SearchableSelect label="Class" placeholder="All classes" value={classId} onChange={setClassId} options={classOptions} minWidth={150} />
        <SearchableSelect label="Subject" placeholder="All subjects" value={subjectId} onChange={setSubjectId} options={subjectOptions} minWidth={170} />
      </FilterBar>

      <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <Tabs value={status} onChange={(_, v) => setStatus(v)} variant="scrollable" sx={{ px: 1.5, borderBottom: 1, borderColor: 'divider', minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600 } }}>
          {TABS.map(t => {
            const n = t === 'all' ? counts.pending + counts.draft + counts.locked : counts[t];
            return (
              <Tab
                key={t}
                value={t}
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <span>{t === 'all' ? 'All' : STATUS[t].tab}</span>
                    <Box component="span" sx={{ px: 0.75, borderRadius: 1, fontSize: 11, bgcolor: alpha(theme.palette.text.primary, 0.06), color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{n}</Box>
                  </Stack>
                }
              />
            );
          })}
        </Tabs>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': tableHeadCellSx }}>
                <TableCell>Exam</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" width={150} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRowSkeletons columns={6} rows={8} />
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 6 }}>
                    <EmptyState
                      icon={<EditNoteOutlined />}
                      title={status !== 'all' ? STATUS[status].empty : filtered ? 'No sheets match' : 'Nothing to mark yet'}
                      description={status !== 'all'
                        ? (filtered ? 'Among the sheets these filters show.' : 'Check the other tabs.')
                        : filtered ? 'Try another exam, class or subject.'
                        : 'Sheets appear here once an exam is published. Teachers see the subjects they are assigned to teach.'}
                    />
                  </TableCell>
                </TableRow>
              ) : rows.map(row => {
                const pct = row.total ? (row.marked / row.total) * 100 : 0;
                const locked = row.status === 'locked';
                return (
                  <TableRow key={row.key} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.examName}</Typography>
                      <Typography variant="caption" color="text.secondary">{dateRange(row.startDate, row.endDate)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{fmtClassName(row.className)}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.sectionName ? `Section ${row.sectionName}` : 'Whole class'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{row.subjectName}</Typography>
                      {row.teachers.length
                        ? <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ maxWidth: 220 }}>{row.teachers.join(', ')}</Typography>
                        : <Typography variant="caption" color="warning.main" component="div">No teacher assigned</Typography>}
                    </TableCell>
                    <TableCell sx={{ minWidth: 170 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LinearProgress variant="determinate" value={pct} color={locked ? 'success' : 'primary'} sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.text.primary, 0.06) }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 44, textAlign: 'right' }}>{row.marked}/{row.total}</Typography>
                      </Stack>
                      <LastEdited value={row.lastEdited} />
                    </TableCell>
                    <TableCell>
                      <ToneChip label={STATUS[row.status].label} tone={STATUS[row.status].tone} icon={locked ? LockOutlined : undefined} />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Download marks PDF">
                        <span>
                          <IconButton size="small" onClick={() => downloadPdf(row)} disabled={downloading === row.key} aria-label="Download marks PDF">
                            <BusyIcon busy={downloading === row.key}><Download fontSize="small" /></BusyIcon>
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Button
                        size="small"
                        variant={!locked && canEnter ? 'contained' : 'outlined'}
                        disableElevation
                        startIcon={!locked && canEnter ? <EditNoteOutlined /> : <VisibilityOutlined />}
                        onClick={() => open(row)}
                        sx={{ ml: 1, minWidth: 84 }}
                      >
                        {!locked && canEnter ? 'Enter' : 'View'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <MarksEntryDialog target={target} onClose={() => setTarget(null)} onSaved={load} />
    </Box>
  );
};

export default EnterMarksPage;
